import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Button } from 'src/skins/common/components/Button';
import { TextInput, CheckboxInput, SelectInput } from 'src/skins/venus/components/inputs';
import { TransactionSummaryModal, TransactionStatusModal, TransactionPinModal } from 'src/skins/venus/modals';
import { formatToDigits } from 'src/utils/helpers';
import { airtimeNetworkOption } from 'src/utils/data';
import { getBillSummary, confirmBillTransaction } from 'src/store/actions/billActions';

export const BuyAirtimeForm = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [transactionStatus, setTransactionStatus] = useState('');
  const [transactionSummary, setTransactionSummary] = useState({});

  const [showAirtimeSummaryModal, setShowAirtimeSummaryModal] = useState(false);
  const [showTransactionStatusModal, setShowTransactionStatusModal] = useState(false);
  const [transactionPinModalStatus, setTransactionPinModalStatus] = useState(false);

  const isLoading = useSelector(state => state.BILLS.isLoading);
  const beneficiary = history.location.state || {};

  const toggleAirtimeSummaryModal = () => setShowAirtimeSummaryModal(!showAirtimeSummaryModal);
  const toggleTransactionPinModalStatus = () => {
    setTransactionPinModalStatus(!transactionPinModalStatus);
  };

  const toggleTransactionStatusModal = () => {
    setShowTransactionStatusModal(!showTransactionStatusModal);
    if (transactionStatus.includes('success')) {
      history.push('/dashboard/bills');
    }
  };

  const { register, handleSubmit, setValue, clearErrors, watch, control, errors } = useForm({
    defaultValues: {
      network: beneficiary.network || '',
      name: beneficiary.name || '',
      mobile: beneficiary.mobile || '',
      saveBeneficiary: false,
    },
  });

  const onSubmit = data => {
    const payload = {
      amount: +data.amount,
      network: data.network || beneficiary.network,
      mobile: +data.mobile || +beneficiary.mobile,
    };

    dispatch(
      getBillSummary(payload, 'airtime', data => {
        if (data) {
          setTransactionSummary(data);
          toggleAirtimeSummaryModal();
          return;
        }
        return;
      })
    );
  };

  const handleConfirmAirtimeClick = pin => {
    const payload = {
      amount: transactionSummary.amount,
      mobile: +transactionSummary.phone,
      network: watch('network'),
      pin,
    };

    if (watch('saveBeneficiary')) {
      payload.saveBeneficiary = watch('saveBeneficiary');
      payload.name = watch('name');
    }

    dispatch(
      confirmBillTransaction(payload, 'airtime', (_success, error) => {
        toggleAirtimeSummaryModal();
        if (error) {
          setTransactionStatus('failed');
          return toggleTransactionStatusModal();
        }
        setTransactionStatus('successful');
        toggleTransactionStatusModal();
        return;
      })
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <Controller
            control={control}
            name="network"
            render={() => (
              <SelectInput
                label="Network"
                name="network"
                ref={register}
                icon="bank"
                required
                isClearable={false}
                disabled={beneficiary.state}
                options={airtimeNetworkOption}
                value={watch('network') || null}
                onChange={selectedOption => {
                  setValue('network', selectedOption?.value || '');
                  clearErrors('network');
                }}
              />
            )}
          />
          <TextInput
            type="text"
            label="Phone Number"
            name="mobile"
            ref={register}
            icon="phone"
            inputmode="numeric"
            pattern="[0-9]*"
            required
            disabled={beneficiary.state}
            onInput={e => {
              e.target.value = formatToDigits(e.target.value, 11);
              return e;
            }}
          />
          <Controller
            control={control}
            name="amount"
            rules={{ min: { value: 100, message: 'minimum amount is 100 naira' } }}
            defaultValue=""
            render={() => (
              <TextInput
                type="text"
                placeholder="Amount"
                label="Amount"
                name="amount"
                ref={register}
                inputmode="numeric"
                pattern="[0-9]*"
                icon="money"
                required
                onInput={e => {
                  let { value } = e.target;
                  value = formatToDigits(value);
                  e.target.value = value.slice(0, 15);
                }}
                errors={
                  <React.Fragment>{errors.amount && <p className="error">{errors.amount.message}</p>}</React.Fragment>
                }
              />
            )}
          />
          <CheckboxInput
            label="Save as beneficiary"
            name="saveBeneficiary"
            ref={register}
            disabled={beneficiary.state}
          />

          {(watch('saveBeneficiary') || beneficiary.state) && (
            <TextInput
              type="text"
              label="Name"
              name="name"
              ref={register}
              icon="account"
              required
              disabled={beneficiary.state}
            />
          )}
        </div>

        <br />

        <Button type="submit" text="Buy Airtime" size="large" isLoading={isLoading.getBillSummary} />
      </form>

      <TransactionSummaryModal
        transfer={transactionSummary}
        isOpen={showAirtimeSummaryModal}
        closeModal={toggleAirtimeSummaryModal}
        handleConfirmTransferClick={toggleTransactionPinModalStatus}
      />

      {transactionPinModalStatus && (
        <TransactionPinModal
          isOpen={transactionPinModalStatus}
          closeModal={toggleTransactionPinModalStatus}
          clickIsLoading={isLoading.confirmBillTransaction}
          handleConfirmTransactionClick={handleConfirmAirtimeClick}
        />
      )}

      <TransactionStatusModal
        isOpen={showTransactionStatusModal}
        closeModal={toggleTransactionStatusModal}
        status={transactionStatus}
      />
    </>
  );
};

export default BuyAirtimeForm;
